import { useNavigate } from 'react-router-dom';
import { FiBarChart2 } from 'react-icons/fi';

import type { SignalDetail } from '../../../types/signals-live';
import BreadcrumbNav from '../shared/BreadcrumbNav';
import IconWrapper from '../../IconWrapper';
import { Button } from '../../ui/button';
import { Heading } from '../../ui/heading';
import { Timestamp } from '../../ui/timestamp';
import { getSignalHeading } from './signalsLiveDetailUtils';

interface SignalDetailHeaderProps {
  signal: SignalDetail;
  isConnected: boolean;
  isOmbudsmanUser: boolean;
  breadcrumbItems: Array<{ label: string; path?: string }>;
  onBack: () => void;
  onViewSimulations: () => void;
}

export function SignalDetailHeader({
  signal,
  isConnected,
  isOmbudsmanUser,
  breadcrumbItems,
  onBack,
  onViewSimulations,
}: SignalDetailHeaderProps) {
  const navigate = useNavigate();
  const quickActions = isOmbudsmanUser ? (
    <Button
      variant="outline"
      size="sm"
      onClick={onViewSimulations}
      className="flex items-center gap-2"
    >
      <IconWrapper icon={FiBarChart2} className="h-4 w-4" />
      View Simulations
    </Button>
  ) : null;

  return (
    <>
      <BreadcrumbNav
        items={breadcrumbItems}
        quickActions={quickActions}
        onBack={onBack}
      />

      <div className="bg-white px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <Heading level={1} className="text-figma-2xl text-figma-text-primary">
              {getSignalHeading(signal)}
            </Heading>
            <div className="mt-2 flex items-center space-x-3">
              <span className="text-figma-sm text-figma-text-secondary">
                #{signal.source_id || signal.id?.slice(0, 8).toUpperCase()}
              </span>
              <span className="text-figma-sm text-figma-text-muted">•</span>
              <span className="text-figma-sm flex items-center text-figma-text-secondary">
                <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Received <Timestamp value={signal.received_at || signal.created_at} format="relative" className="ml-1" />
              </span>
              {isConnected && (
                <>
                  <span className="text-figma-sm text-figma-text-muted">•</span>
                  <span className="text-figma-sm flex items-center text-green-600">
                    <span className="mr-1 h-2 w-2 animate-pulse rounded-full bg-green-600" />
                    Live
                  </span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={() => navigate('/v4/records')}
            className="p-2 text-figma-text-muted transition-colors hover:text-figma-text-secondary"
            title="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}
